import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "./components/ui/toaster";
import { TooltipProvider } from "./components/ui/tooltip";
import NotFound from "./pages/not-found";
import Home from "./pages/home";
import Login from "./pages/login";
import Register from "./pages/register";
import Profile from "./pages/profile";
import { ForgotPasswordForm } from "./components/ForgotPasswordForm";
import { ResetPasswordForm } from "./components/ResetPasswordForm";
import BrowseShows from "./pages/browse-shows";
import BrowseArtists from "./pages/browse-artists";
import BrowseVenues from "./pages/browse-venues";
import BrowseMarkets from "./pages/browse-markets";
import BrowseMap from "./pages/browse-map";
import Plans from "./pages/plans";
import DataImport from "./pages/data-import";

import EnhancedCalculator from "./pages/enhanced-calculator";
import PlanDetail from "./pages/plan-detail";
import AdminProfile from "./pages/admin-profile";
import AdminQuickPersonas from "./pages/admin-quick-personas";
import AdminVenues from "./pages/admin-venues";
import AdminVenueImages from "./pages/admin-venue-images";
import AdminCalculatorSettings from "./pages/admin-calculator-settings";
import AdminIdeas from "./pages/admin-ideas";
import DynamicValuesManagement from "./pages/admin/dynamic-values";
import AdminDashboard from "./pages/admin-dashboard";
import { AuthProvider } from "./lib/auth.tsx";
import { PricingVisibilityProvider } from "./lib/pricing-visibility";
import { PersonaProvider } from "./contexts/persona-context";
import { PlanSidebarProvider } from "./contexts/plan-sidebar-context";
import ErrorBoundary from "./components/error-boundary";
import SkipNavigation from "./components/skip-navigation";
import Navbar from "./components/navbar";
import Footer from "./components/footer";
import HelpCenter from "./pages/help-center";
import ContactUs from "./pages/contact-us";
import PrivacyPolicy from "./pages/privacy-policy";
import ArtistDetailPage from "./pages/artist-detail";
import VenueDetailPage from "./pages/venue-detail";
import MarketDetailPage from "./pages/market-detail";
import PersonasPage from "./pages/personas";
import PersonaDetailPage from "./pages/persona-detail";
import { useScrollRestoration } from "./hooks/use-scroll-restoration";


function RouterWithScrollRestoration() {
  useScrollRestoration();
  
  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/login" component={Login} />
      <Route path="/register" component={Register} />
      <Route path="/forgot-password" component={ForgotPasswordForm} />
      <Route path="/reset-password" component={ResetPasswordForm} />
      <Route path="/profile" component={Profile} />
      <Route path="/browse" component={() => { window.location.replace('/browse-artists'); return null; }} />
      <Route path="/browse-artists" component={BrowseArtists} />
      <Route path="/artist/:id" component={ArtistDetailPage} />
      <Route path="/browse-shows" component={BrowseShows} />
      <Route path="/browse-venues" component={BrowseVenues} />
      <Route path="/venue/:id" component={VenueDetailPage} />
      <Route path="/browse-markets" component={BrowseMarkets} />
      <Route path="/market/:id" component={MarketDetailPage} />
      {/* TEMPORARILY HIDDEN - Map view under development */}
      {/* <Route path="/browse-map" component={BrowseMap} /> */}
      <Route path="/plans" component={Plans} />
      <Route path="/plans/:id" component={PlanDetail} />
      <Route path="/personas" component={PersonasPage} />
      <Route path="/persona/new" component={PersonaDetailPage} />
      <Route path="/persona/:id" component={PersonaDetailPage} />
      <Route path="/calculator" component={EnhancedCalculator} />
      <Route path="/calculator-basic" component={() => { window.location.replace('/calculator'); return null; }} />
      <Route path="/help-center" component={HelpCenter} />
      <Route path="/contact-us" component={ContactUs} />
      <Route path="/privacy-policy" component={PrivacyPolicy} />

      <Route path="/admin" component={AdminDashboard} />
      <Route path="/admin/profile" component={AdminProfile} />
      <Route path="/admin/quick-personas" component={AdminQuickPersonas} />
      <Route path="/admin/dynamic-values" component={DynamicValuesManagement} />
      <Route path="/admin/import" component={DataImport} />
      <Route path="/admin/venues" component={AdminVenues} />
      <Route path="/admin/venue-images" component={AdminVenueImages} />
      <Route path="/admin/calculator" component={AdminCalculatorSettings} />
      <Route path="/admin/ideas" component={AdminIdeas} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <PricingVisibilityProvider>
            <PersonaProvider>
              <PlanSidebarProvider>
              <TooltipProvider>
              <SkipNavigation />
              <Navbar />
              <main id="main-content" className="pt-16 min-h-screen"> {/* Account for fixed navbar */}
                <RouterWithScrollRestoration />
              </main>
              <Footer />
              <Toaster />
              </TooltipProvider>
              </PlanSidebarProvider>
            </PersonaProvider>
          </PricingVisibilityProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
